import type { PointerEvent } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion, useSpring } from 'framer-motion';
import type { ProjectSummaryDto } from '@portfolio/shared';
import { cn } from '@/lib/utils';
import { useLocale } from './i18n';

export function ProjectCover({
  project,
  className,
  sizes = '(min-width: 1024px) 50vw, 100vw',
}: {
  project: Pick<ProjectSummaryDto, 'title' | 'coverImage' | 'accentColor'>;
  className?: string;
  sizes?: string;
}) {
  const img = project.coverImage;
  if (!img) {
    return (
      <div
        className={cn('flex items-end p-6', className)}
        style={{ backgroundColor: project.accentColor ?? '#F7F7F7' }}
        role="img"
        aria-label={project.title}
      >
        <span className="text-4xl font-black tracking-[-0.005em] text-white/90 mix-blend-difference">
          {project.title}
        </span>
      </div>
    );
  }
  return (
    <img
      src={img.url}
      srcSet={img.thumbUrl ? `${img.thumbUrl} 720w, ${img.url} ${img.width ?? 2400}w` : undefined}
      sizes={img.thumbUrl ? sizes : undefined}
      alt={img.alt ?? project.title}
      width={img.width ?? undefined}
      height={img.height ?? undefined}
      loading="lazy"
      decoding="async"
      className={cn('object-cover', className)}
    />
  );
}

/** "2024 – Present" when set, otherwise the year. */
export const projectPeriod = (p: Pick<ProjectSummaryDto, 'period' | 'year'>) =>
  p.period ?? (p.year ? String(p.year) : null);

/**
 * Dark cover on top, title on white below. On hover the card tilts a few degrees towards the
 * pointer, the cover zooms slightly and the text nudges — small movements in place, so the grid
 * feels alive without anything jumping around.
 */
export function ProjectCard({ project }: { project: ProjectSummaryDto }) {
  const reduce = useReducedMotion();
  const rx = useSpring(0, { stiffness: 180, damping: 18 });
  const ry = useSpring(0, { stiffness: 180, damping: 18 });
  const period = projectPeriod(project);
  const { to } = useLocale();

  const onPointerMove = (e: PointerEvent<HTMLElement>) => {
    if (reduce || e.pointerType !== 'mouse') return;
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 6);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 6);
  };
  const onPointerLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <div style={{ perspective: 1200 }}>
      <motion.div
        style={{ rotateX: rx, rotateY: ry }}
        onPointerMove={onPointerMove}
        onPointerLeave={onPointerLeave}
        className="group overflow-hidden rounded-[28px] bg-card shadow-[0_30px_60px_-24px_rgb(30_30_30/0.3)] transition-shadow duration-500 hover:shadow-[0_40px_70px_-24px_rgb(30_30_30/0.4)] md:rounded-[40px]"
      >
        <Link to={to(`/work/${project.slug}`)} className="block focus-visible:outline-offset-4">
          <div className="relative z-10 aspect-[546/322] overflow-hidden bg-gradient-to-b from-night to-[#444444] shadow-[0_22px_32px_-16px_rgb(0_0_0/0.28)]">
            {project.coverImage && (
              <>
                <ProjectCover
                  project={project}
                  className="absolute inset-0 size-full transition-transform duration-[900ms] ease-[var(--ease-out-expo)] group-hover:scale-[1.05]"
                />
                <span className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/55 to-transparent" />
              </>
            )}
            {period && (
              <span className="absolute start-5 bottom-4 text-ui text-white/80 transition-transform duration-500 ease-[var(--ease-out-expo)] group-hover:-translate-y-1 md:start-[31px] md:bottom-[22px] md:text-[23px]">
                {period}
              </span>
            )}
          </div>
          <h3 className="max-w-[9.5em] px-5 pt-7 pb-9 text-title leading-none font-bold tracking-[-0.005em] text-balance text-strong transition-transform duration-500 ease-[var(--ease-out-expo)] group-hover:translate-x-1.5 rtl:group-hover:-translate-x-1.5 md:px-[31px] md:pt-[49px] md:pb-[52px] md:text-[3.2rem]">
            {project.title}
          </h3>
        </Link>
      </motion.div>
    </div>
  );
}
